import { zoneToXY } from '../../lib/afl/field-coords.js';
import type { OnFieldCardView } from './AflView.js';

/**
 * Renders the player cards that are standing on the ground, positioned in
 * their zones. The ball-carrier is shown largest with a football badge and
 * an amber ring; supporting players are normal; beaten contestants are
 * dimmed. Players sharing a zone are fanned out so they don't fully
 * overlap. Colours follow the seat: indigo for the viewer, red for the
 * opponent.
 *
 * Lives inside the Field's `overlay` layer, so every child positions
 * itself with absolute left/top percentages matching the ball overlay.
 */
export function OnFieldPlayers({
  onField,
  attackingRight,
  isYouSeat,
  onCardClick,
}: {
  onField: OnFieldCardView[];
  attackingRight: boolean;
  /** True when the given seat id belongs to the viewer. */
  isYouSeat: (seatId: string) => boolean;
  /**
   * Make the on-field cards tappable (view the card / use it if it has a
   * legal action). When set, each card re-enables pointer events that the
   * overlay layer otherwise suppresses.
   */
  onCardClick?: (card: OnFieldCardView) => void;
}) {
  if (!onField.length) return null;

  // Group by zone so we can fan out players that share a cell.
  const byZone = new Map<string, OnFieldCardView[]>();
  for (const item of onField) {
    const key = `${item.line}:${item.lane}`;
    const list = byZone.get(key) ?? [];
    list.push(item);
    byZone.set(key, list);
  }

  const nodes: React.ReactNode[] = [];
  for (const [, group] of byZone) {
    // Render the carrier last (on top); fan the group horizontally.
    const ordered = [...group].sort((a, b) => Number(a.isCarrier) - Number(b.isCarrier));
    const n = ordered.length;
    ordered.forEach((item, i) => {
      const { x, y } = zoneToXY(item.line, item.lane, attackingRight);
      const spread = n > 1 ? (i - (n - 1) / 2) * 7 : 0; // % x fan
      const lift = n > 1 && !item.isCarrier ? -4 : 0; // nudge supports up a touch
      const mine = isYouSeat(item.seatId);
      nodes.push(
        <div
          key={item.cardId}
          className="absolute transition-[left,top] duration-500 ease-out will-change-[left,top] motion-reduce:transition-none"
          style={{
            left: `${x + spread}%`,
            top: `${y + lift}%`,
            transform: 'translate(-50%, -50%)',
            zIndex: item.isCarrier ? 20 : 10,
          }}
        >
          {onCardClick ? (
            <button
              type="button"
              onClick={() => onCardClick(item)}
              title={`${item.card?.name ?? 'Player'} — view card`}
              className="pointer-events-auto block animate-pop-in cursor-pointer rounded-md transition hover:-translate-y-0.5 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-ec-brass motion-reduce:animate-none"
            >
              <OnFieldPlayer item={item} mine={mine} />
            </button>
          ) : (
            <div className="animate-pop-in motion-reduce:animate-none">
              <OnFieldPlayer item={item} mine={mine} />
            </div>
          )}
        </div>,
      );
    });
  }

  return <>{nodes}</>;
}

function abbrevPosition(position?: string): string {
  switch (position) {
    case 'forward':
      return 'FWD';
    case 'midfielder':
      return 'MID';
    case 'ruck':
      return 'RUC';
    case 'back':
      return 'BCK';
    default:
      return (position ?? '—').slice(0, 3).toUpperCase();
  }
}

function OnFieldPlayer({ item, mine }: { item: OnFieldCardView; mine: boolean }) {
  const card = item.card;
  const name = card?.name ?? 'Player';
  const pos = abbrevPosition(card?.position);
  const power = card?.stats?.power;
  const carrier = item.isCarrier;
  const beaten = item.role === 'beaten';

  // Mini-cards follow the big-card language: bone face, accent frame. The
  // SEAT colour lives in the header strip + the frame edge (you = steel,
  // opponent = oxblood) so the two sides read apart at a glance; the carrier
  // earns a brass pulse-ring; a beaten contestant dims.
  const seatColor = mine ? 'var(--ec-pos-ruck)' : 'var(--ec-pos-forward)';
  const headBg = mine
    ? 'bg-gradient-to-r from-[#42535f] to-ec-ruck'
    : 'bg-gradient-to-r from-ec-oxblood-deep to-ec-oxblood';
  const glow = carrier
    ? 'animate-pulse-ring shadow-[0_0_12px_-1px_rgba(245,168,28,0.85)] motion-reduce:animate-none'
    : 'shadow-md shadow-black/50';
  const scale = carrier ? 'scale-110' : '';
  const dim = beaten ? 'opacity-40 saturate-50' : '';

  return (
    <div
      title={`${name} · ${pos}${power != null ? ` · power ${power}` : ''}${carrier ? ' · has the ball' : ''}`}
      style={{ borderColor: seatColor }}
      className={`relative w-[clamp(2.6rem,7vw,3.4rem)] select-none overflow-hidden rounded-md border-2 bg-ec-face ${glow} ${scale} ${dim}`}
    >
      {carrier && (
        <span className="absolute -right-1 -top-1 z-10 rounded-full bg-ec-brass px-1 text-[8px] leading-tight text-ec-ink shadow ring-1 ring-ec-ink/40">
          🏉
        </span>
      )}
      <div className={`flex items-center justify-between px-1 py-px font-display text-[8px] font-bold uppercase tracking-wide text-ec-face [text-shadow:0_1px_1px_rgba(19,22,27,0.4)] ${headBg}`}>
        <span>{pos}</span>
        {power != null && <span className="tabular-nums">{power}</span>}
      </div>
      <div className="truncate px-1 py-0.5 text-center font-display text-[9px] font-semibold leading-tight text-ec-ink">
        {name}
      </div>
    </div>
  );
}
